import { IDestinationDef } from '@stores/destinationDefsList';
import { ISourceStore } from '@stores/source';
import { DestinationConfig } from '@stores/destination';
import { split } from 'lodash';
import { ITransformationStore } from '@stores/transformation';
import { ParsedQuery } from 'query-string';
import SourceFactory from '@components/sources/sourceFactory';
import { CatchErr, RecursiveArg } from '@utils/types';

type SourceType = { category?: string; name: string };

const getSourceType = (sourceDef: SourceType): string =>
  SourceFactory(sourceDef.category).getSourceType(sourceDef);

const getModifiedDestinationConfig = (
  destinationDef: IDestinationDef,
  config: DestinationConfig,
  selectedSources: { sourceDef: SourceType }[] | undefined,
) => {
  if (!selectedSources) {
    return config;
  }
  const selectedSourceTypes: string[] = [];
  selectedSources.forEach((source: { sourceDef: SourceType }) => {
    const sourceType = getSourceType(source.sourceDef);
    if (!selectedSourceTypes.includes(sourceType)) {
      selectedSourceTypes.push(sourceType);
    }
  });
  const { destConfig } = destinationDef.config!;
  selectedSourceTypes.forEach((sourceType: string) => {
    const sourceTypeKeys = destConfig && destConfig[sourceType];
    if (sourceTypeKeys) {
      sourceTypeKeys.forEach((sourceTypeKey: string) => {
        const key = `${sourceType}-${sourceTypeKey}`;
        if (config[sourceTypeKey] === undefined) {
          config[sourceTypeKey] = {};
        }
        const obj = config[sourceTypeKey];
        if (typeof obj === 'object') {
          obj[sourceType] = config[key];
        }
        delete config[key];
      });
    }
  });
  return config;
};

const trimConfigStrings = (config: RecursiveArg) => {
  if (Array.isArray(config)) {
    return config.reduce((acc, item) => {
      acc.push(trimConfigStrings(item));
      return acc;
    }, []);
  }
  if (typeof config === 'object') {
    return Object.keys(config).reduce((acc: DestinationConfig, key) => {
      acc[key] = trimConfigStrings(config[key]);
      return acc;
    }, {});
  }
  if (typeof config === 'string') {
    return config.trim();
  }
  return config;
};

const findIfDeviceModeOnly = (destinationDefinition?: IDestinationDef) => {
  const isDeviceModeOnly = !destinationDefinition?.config?.supportedSourceTypes?.includes('cloud');
  return isDeviceModeOnly;
};

const isValidInput = (regexString: string, value: string): boolean => {
  const regex = RegExp(regexString);
  return regex.test(value);
};

const isSourceNameUnique = (name: string, sourcesList: ISourceStore[]): boolean => {
  const isNotUniqueName = sourcesList.some(
    (source: ISourceStore) => source.name.toLowerCase() === name.toLowerCase(),
  );
  return !isNotUniqueName;
};

const isTransformationNameUnique = (name: string, list: ITransformationStore[]): boolean => {
  const isNotUniqueName = list.some(
    (trfm: ITransformationStore) => trfm.name.toLowerCase() === name.toLowerCase(),
  );
  return !isNotUniqueName;
};

const isKeyPressed = (e: React.KeyboardEvent<HTMLElement>, key: KEYS): boolean => e.keyCode === key;

enum KEYS {
  ENTER = 13,
  ESCAPE = 27,
  SPACE = 32,
}

const getParsedValue = (p: ParsedQuery, s: string): string => {
  if (!p[s]) {
    return '';
  }
  if (typeof p[s] === 'string') {
    return p[s]!.toString();
  }
  if (Array.isArray(p[s]) && p[s]!.length > 0) {
    return p[s]![0]!;
  }
  return '';
};

const isValidJson = (e: string): boolean => {
  try {
    JSON.parse(e);
    return true;
  } catch (e) {
    return false;
  }
};

const splitString = (e: string, separator: string) =>
  split(e.trim(), separator).map((s) => s.trim());

const getApiErrorMessage = (error: CatchErr, fallback: string) => {
  const is4xxError = error.response?.status >= 400 && error.response?.status < 500;
  if (is4xxError && error.response?.data?.message) {
    return `${fallback} - ${error.response?.data?.message}`;
  }
  return fallback;
};

const belongsToDomain = (email: string, domains: string[]) =>
  domains.some((domain) => email.endsWith(domain));

const isRudderUser = (email: string) =>
  belongsToDomain(email, ['@rudderlabs.com', '@rudderstack.com']);

const isOAuthSupported = (destinationDefinition: IDestinationDef | undefined) => {
  const authType = destinationDefinition?.config?.auth?.type;
  return authType && authType === 'OAuth';
};
const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1);

const isLocalhost = () => window.location.host.includes('localhost');

export {
  getSourceType,
  getModifiedDestinationConfig,
  trimConfigStrings,
  findIfDeviceModeOnly,
  isValidInput,
  isSourceNameUnique,
  isTransformationNameUnique,
  isValidJson,
  isKeyPressed,
  getApiErrorMessage,
  KEYS,
  getParsedValue,
  splitString,
  isOAuthSupported,
  capitalize,
  // ts-prune-ignore-next
  isRudderUser,
  isLocalhost,
};
